import { z } from 'zod'
import { isoDateTime, uuid } from './common.js'

/**
 * Vouchers, insurance certificates, tickets. Upload, sensitivity gate, unlock
 * flow, enforced retention — build unit 7.9.
 *
 * **There is no identity category here, and that is load-bearing.** Passports,
 * ID cards and visas are six fields in `travel_documents` (7.16), never a
 * picture — see spec §5 and §6.9. Adding a value to `fileCategory` that names
 * an identity document is exactly the mistake this enum exists to make
 * unrepresentable. If you are tempted, the reasoning is in PATTERNS.md §6.
 */
export const fileCategory = z.enum(['voucher', 'insurance', 'ticket', 'other'])
export type FileCategory = z.infer<typeof fileCategory>

export const fileSensitivity = z.enum(['sensitive', 'shareable'])
export type FileSensitivity = z.infer<typeof fileSensitivity>

/**
 * Content types this system will store. Deliberately narrow: a travel
 * document is a scan or a PDF, never an executable, a script, or an office
 * document format capable of carrying macros.
 */
export const ALLOWED_FILE_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

/** 20 MiB. A phone-camera photo of a printed voucher fits comfortably under this. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024

/**
 * The unlock code a traveller types to read a sensitive file's bytes.
 *
 * Never logged, never returned by any read endpoint, hashed at rest. Kept
 * short enough that Ana can read it aloud on a phone call — this is a shared
 * secret handed over out of band, not a password.
 */
export const unlockCode = z.string().trim().min(4).max(64)

/**
 * Fields that ride alongside the multipart upload. The bytes themselves are
 * not modelled here — `@fastify/multipart` hands the route a stream/buffer and
 * a client-declared content type, which the service validates against
 * `ALLOWED_FILE_CONTENT_TYPES` before anything touches disk.
 *
 * `sensitivity` defaults to `'sensitive'` to match the column default —
 * unknown means sensitive, end to end. A sensitive file with no code is
 * rejected by the service: fail closed has to hold at the moment of creation,
 * not only at the moment of read.
 */
export const uploadFileFields = z.object({
  category: fileCategory.default('other'),
  sensitivity: fileSensitivity.default('sensitive'),
  unlockCode: unlockCode.optional(),
})
export type UploadFileFields = z.infer<typeof uploadFileFields>

/** What a caller submits to read a sensitive file's bytes. Absent for a shareable one. */
export const downloadFileRequest = z.object({
  code: z.string().max(64).optional(),
})
export type DownloadFileRequest = z.infer<typeof downloadFileRequest>

/** The advisor's view: everything, including retention. */
export const tripFile = z.object({
  id: uuid,
  tripId: uuid,
  filename: z.string(),
  contentType: z.string(),
  byteSize: z.number().int().nonnegative(),
  category: fileCategory,
  sensitivity: fileSensitivity,
  /**
   * Trip end + 6 months by default, computed server-side at upload time —
   * never at ticketing, and never left for a caller to set. Nullable in the
   * column only because the table predates this rule being enforced in code;
   * the service always fills it in.
   */
  purgeAfter: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type TripFile = z.infer<typeof tripFile>

/**
 * What a share-link holder is told about a file. No `purgeAfter` — `itin_share`
 * holds no grant on that column, and this shape mirrors the grant exactly
 * rather than merely agreeing with it by convention.
 */
export const shareTripFile = tripFile.omit({ purgeAfter: true })
export type ShareTripFile = z.infer<typeof shareTripFile>
